import {
  CreateFormTableRowButton,
  DataObjectCommentDto,
  DataObjectVersionType,
  DataVersionStatus,
  DeleteFormTableRowButton,
  ExpandableLongText,
  formatDate,
  formatDateTimeShort,
  FormDataObjectVersionSelect,
  FormField,
  FormInput,
  FormPeriodEndPicker,
  getDayjs,
  getFormTableFieldName,
  getFormTableFieldPath,
  GroupDescriptionOption,
  isDisabledDate,
  ISO_DATE_FORMAT,
  ISODate,
  TanstackFormTable,
  useDataObjectVersionsQuery,
  User,
  useUserInfo,
  valueDate,
} from '@atlas-frontend/sdk';
import { Button, Flex } from 'antd';
import translations from './CommentsTable.translation.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { CellContext, createColumnHelper, Row } from '@tanstack/react-table';
import { FC, memo, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { colorTextSecondary } from '@atlas-frontend/design-tokens';
import { useFormContext, useWatch } from 'react-hook-form';
import { WithDescriptionId } from './CommentsModal';
import { Dayjs } from 'dayjs';
import { VersionCell } from '../../output-data/VersionCell';

const { addTranslations, t } = i18nNsFactory(
  'visual-validation-comments-table',
);
addTranslations(translations);

const columnHelper = createColumnHelper<DataObjectCommentDto>();

type BaseProps = {
  descriptionOption: GroupDescriptionOption & WithDescriptionId;
};

export type SingleProps = BaseProps & {
  type: 'single';
  valueDate?: ISODate | null;
  valueDates?: never;
};

export type CompareOrRangeProps = BaseProps & {
  type: 'compare' | 'range';
  valueDates?: [ISODate | null, ISODate | null];
  valueDate?: never;
};

export type CommentsTableProps = SingleProps | CompareOrRangeProps;

export const CommentsTable: React.FC<CommentsTableProps> = ({
  descriptionOption,
  type,
  valueDate,
  valueDates,
}) => {
  const { user } = useUserInfo();
  const { defaultRowValues, isLoading } = useDefaultRowValues(
    descriptionOption,
    user,
  );
  const [defaultFilters] = useState(() =>
    getDefaultFilters(type, valueDate, valueDates),
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor((rowData) => formatDate(rowData.valueDate), {
        id: 'valueDate',
        header: t('column.valueDate'),
        cell: (props) => (
          <ValueDateCell {...props} descriptionOption={descriptionOption} />
        ),
        filterFn: 'commentValueDateSelect',
        meta: {
          width: '20ch',
          frequency: descriptionOption.frequency,
        },
      }),
      columnHelper.accessor('dataObjectVersionId', {
        header: t('column.version'),
        meta: {
          width: '20ch',
        },
        cell: (props) => (
          <VersionSelectCell {...props} descriptionOption={descriptionOption} />
        ),
        sortingFn: sortByVersion,
        enableColumnFilter: false,
      }),
      columnHelper.accessor('createdBy.username', {
        id: 'createdBy',
        header: t('column.createdBy'),
        meta: {
          width: '15ch',
        },
      }),
      columnHelper.accessor('comment', {
        header: t('column.comment'),
        enableSorting: false,
        cell: (props) => {
          const name = getFormTableFieldName(props);
          props.row.original.createdBy;
          return (
            <FormField
              name={name}
              size="small"
              preview={{
                previewOnly: !isEditable(props, user.id),
                ellipsis: false,
                render: (value) => (
                  <ExpandableLongText hideExpandButton>
                    {value}
                  </ExpandableLongText>
                ),
              }}
            >
              <FormInput autoFocus name={name} maxLength={2000} rows={3} />
            </FormField>
          );
        },
      }),
      columnHelper.accessor('confirm', {
        header: t('column.confirm.label'),
        enableSorting: false,
        filterFn: 'includesOption',
        cell: (props) => {
          const index = props.row.index;
          if (!props.row.original.confirm) return null;
          return (
            <StyledButton
              id={`comment-type-button-${index}`}
              size="small"
              shape="circle"
              icon={<span>K</span>}
            />
          );
        },
        meta: {
          width: '10ch',
        },
      }),
      columnHelper.accessor(
        (rowData) => formatDateTimeShort(rowData.createdAt),
        {
          id: 'createdAt',
          header: t('column.createdAt'),
          filterFn: 'formattedDate',
          meta: {
            width: '22ch',
          },
        },
      ),
      columnHelper.accessor(
        (rowData) => formatDateTimeShort(rowData.modifiedAt),
        {
          id: 'modifiedAt',
          header: t('column.modifiedAt'),
          filterFn: 'formattedDate',
          meta: {
            width: '28ch',
          },
        },
      ),
      columnHelper.display({
        id: 'operations',
        header: () => {
          return (
            <Flex justify="center" gap="small">
              <CreateFormTableRowButton
                title={t('button.addComment')}
                autoFocusField="comment"
                defaultRowValues={defaultRowValues}
              />
            </Flex>
          );
        },
        cell: (props) => {
          const isDeletable =
            isEditable(props, user.id) && !props.row.original.confirm;
          if (!isDeletable) return null;
          return (
            <Flex justify="center" gap="small">
              <DeleteFormTableRowButton size="small" context={props} />
            </Flex>
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
        meta: {
          width: 50,
          className: 'sticky-right',
        },
      }),
    ],
    [defaultRowValues, descriptionOption, user.id],
  );

  const options = useMemo(
    () => ({
      confirm: [
        { id: true, name: t('column.confirm.confirmed') },
        { id: false, name: t('column.confirm.other') },
      ],
    }),
    [],
  );

  return (
    <TanstackFormTable
      name="comments"
      columns={columns}
      options={options}
      persistColumnStyle={false}
      columnResizeDisabled
      isLoading={isLoading}
      filters={defaultFilters}
    />
  );
};

const ValueDateCell: FC<
  CellContext<DataObjectCommentDto, any> & {
    descriptionOption: GroupDescriptionOption;
  }
> = memo((props) => {
  const name = getFormTableFieldName(props);

  const option = props.descriptionOption;
  const validPeriods = option.activePeriods.map(
    (period) =>
      [getDayjs(period.validFrom), getDayjs(period.validTo)] as [
        Dayjs | null,
        Dayjs | null,
      ],
  );

  return (
    <FormField
      name={name}
      size="small"
      preview={{
        previewOnly: !!props.row.original.id, // existing rows are not editable
        render: (value) => formatDate(value),
      }}
    >
      <FormPeriodEndPicker
        name={name}
        size="small"
        autoFocus
        disabledDate={(date) => isDisabledDate(date, validPeriods)}
        frequency={option.frequency}
        allowClear={true}
      />
    </FormField>
  );
});

const VersionSelectCell: FC<
  CellContext<DataObjectCommentDto, any> & {
    descriptionOption: GroupDescriptionOption & WithDescriptionId;
  }
> = memo((props) => {
  const option = props.descriptionOption;
  const name = getFormTableFieldName(props);
  const path = getFormTableFieldPath(props);
  const basePath = path.slice(0, -1);
  const { setValue } = useFormContext();

  const selectedVersionId = useWatch({
    name,
  }) as number | null;
  const valueDate = useWatch({
    name: [...basePath, 'valueDate'].join('.'),
  }) as ISODate | null;

  const isNewRow = !props.row.original.id;
  const { data: versions, isFetching: isFetching } = useDataObjectVersionsQuery(
    {
      id: option.descriptionId,
      valueDate: valueDate || undefined,
    },
    {
      enabled: !!valueDate && isNewRow, // only enabled for new rows
    },
  );

  useEffect(() => {
    if (!isNewRow) return;

    // If no value date, clear selection
    if (!valueDate) {
      if (selectedVersionId !== null) setValue(name, null);
      return;
    }

    if (!versions) return;

    // If no versions found, clear selection
    if (versions.length === 0) {
      if (selectedVersionId !== null) setValue(name, null);
      return;
    }

    const defaultVersionId = getDefaultVersionId(versions);
    const selectionValid =
      selectedVersionId == null ||
      versions.some((v) => v.id === selectedVersionId);

    if (!selectionValid) {
      // if selected is invalid, set the default if not already selected
      if (selectedVersionId !== defaultVersionId) {
        setValue(name, defaultVersionId);
      }
    }
  }, [isNewRow, versions, selectedVersionId, setValue, name, valueDate]);

  return (
    <FormField
      name={name}
      size="small"
      preview={{
        previewOnly: !!props.row.original.id || !versions?.length, // existing rows are not editable
        render: (value) => {
          const version = versions?.find((v) => v.id === value);
          const versionNumber = isNewRow
            ? version?.versionNumber
            : props.row.original.versionNumber;
          const minorNumber = isNewRow
            ? version?.minorNumber
            : props.row.original.minorNumber;
          const versionStatus = isNewRow
            ? version?.statusCode || null
            : props.row.original.versionStatus;
          return (
            <VersionCell
              versionNumber={versionNumber}
              minorNumber={minorNumber}
              versionStatus={versionStatus as DataVersionStatus}
            />
          );
        },
      }}
    >
      <FormDataObjectVersionSelect
        name={name}
        size="small"
        versions={versions}
        renderCalculationTimeInLabel={false}
        isLoading={isFetching}
        allowClear
      />
    </FormField>
  );
});

const isEditable = (
  props: CellContext<DataObjectCommentDto, any>,
  userId: number,
) => !props.row.original.id || props.row.original.createdBy?.id === userId;

const getDefaultFilters = (
  type: CommentsTableProps['type'],
  valueDate: ISODate | null | undefined,
  valueDates: CommentsTableProps['valueDates'],
) => [
  {
    id: 'valueDate',
    value: {
      type,
      valueDates:
        type === 'single'
          ? [formatDate(valueDate) || null, null]
          : valueDates?.map((date) => formatDate(date)) || [null, null],
    },
  },
];

const useDefaultRowValues = (option: GroupDescriptionOption, user: User) => {
  const defaultValueDate = getDefaultValueDate(option);
  const { data: defaultVersions, isFetching } = useDataObjectVersionsQuery(
    {
      id: option.descriptionId!,
      valueDate: defaultValueDate || undefined,
    },
    {
      enabled: !!defaultValueDate,
    },
  );
  const defaultVersionId = getDefaultVersionId(defaultVersions || []);

  const defaultRowValues = useMemo(
    () => ({
      id: null,
      valueDate: defaultValueDate,
      dataObjectVersionId: defaultVersionId,
      comment: '',
      createdBy: user.id,
    }),
    [defaultValueDate, defaultVersionId, user.id],
  );

  return {
    defaultRowValues,
    isLoading: isFetching,
  };
};

const getDefaultVersionId = (versions: DataObjectVersionType[]) =>
  versions.reduce((max, v) => (v.id > max ? v.id : max), 0) || null;

const getDefaultValueDate = (
  option: GroupDescriptionOption,
): ISODate | null => {
  const validRanges = option.activePeriods.map(
    (period) =>
      [getDayjs(period.validFrom), getDayjs(period.validTo)] as [
        Dayjs | null,
        Dayjs | null,
      ],
  );

  const defaultValueDate = valueDate(null, option.frequency, validRanges);

  return defaultValueDate
    ? (defaultValueDate.format(ISO_DATE_FORMAT) as ISODate)
    : null;
};

const sortByVersion = (
  row1: Row<DataObjectCommentDto>,
  row2: Row<DataObjectCommentDto>,
) =>
  (row1.original.versionNumber ?? -1) - (row2.original.versionNumber ?? -1) ||
  (row1.original.minorNumber ?? -1) - (row2.original.minorNumber ?? -1);

const StyledButton = styled(Button)`
  pointer-events: none;
  color: ${colorTextSecondary};
`;
